// @givmo/compliance — a filing's request for signatures as its sender follows it on the workbench
// (CCM-R1-SIGN-WEB-B; slice 2 §6(d), (e), frames 41–44 and 46, approved as shown, D-CCM-22).
//
// • Out for signature, expired or cancelled: the status card leads the workbench — who sent it and
//   when, each place's signer and state in words (never in color), the lines a state needs (a
//   decline with its reason, a signer who can no longer sign, D-SIGN-12's signature that stands),
//   the "…" menu (extend the date, change the signer or title, cancel) for a sender, and once the
//   request has ended, Send for signature again.
// • Completed: the signed form (the panel's four lines, the export) and its signing record (the
//   record's own served lines, its two downloads) side by side.
//
// Each act is the sender's alone (`compliance:send_for_signature`) and takes no sign-in check; the
// server decides, and a refusal shows in its own words.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  Dialog,
  ErrorMessage,
  Icon,
  IconButton,
  Menu,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { Download, MoreHorizontal } from 'lucide-react';
import { useCompliance } from './compliance-context';
import { isActConflict, serverMessage } from './compliance-decode';
import { ComplianceDateField } from './compliance-date-field';
import { CANCEL } from './compliance-preparation-model';
import {
  decodeSigningFile,
  decodeSigningRecordLines,
  type SigningRequestDetail,
} from './compliance-signing-decode';
import { declinedPaperLine } from './compliance-signed-outside-model';
import { KEEP_IT, OPEN_FAILED } from './compliance-signing-model';
import {
  CANCEL_AND_CHANGE,
  CANCEL_THE_REQUEST,
  CANCEL_THIS_REQUEST,
  CHANGE_AN_ANSWER_TITLE,
  CHANGE_THE_SIGNER,
  DOWNLOAD_RECORD_JSON,
  DOWNLOAD_RECORD_PDF,
  EXPORT_EVERYTHING,
  EXTEND,
  EXTEND_THE_DATE,
  MORE_FOR_THIS_FILING,
  SEND_FOR_SIGNATURE,
  SIGNING_RECORD,
  SIGN_BY,
  THE_SIGNED_FORM,
  cancelRequestBody,
  cancelledLine,
  cantSignLine,
  changeAnswerBody,
  currentSignByDay,
  declinedLine,
  expiredLine,
  latestActOn,
  nameOf,
  placeState,
  placeSubLine,
  sentLine,
  signByInstant,
  signedOnLine,
  signedThenLostLine,
  statusHeading,
} from './compliance-sending-model';
import { signingRecordQueryKey } from './compliance-queries';
import {
  MORE_FOR_THIS_RECORD,
  REMOVE_SIGN_IN_DETAILS,
  RemoveSignInDetailsDialog,
} from './signing-identity-removal';

const PREFIX = 'compliance-request';

/** Built: an act that failed with no words of its own. */
const RECORD_FAILED = 'We couldn’t record that. Please try again.';

/** One line under a request's places; `paperRoute` marks W17, drawn tight under its decline. */
export interface StatusLine {
  text: string;
  paperRoute?: true;
}

/**
 * The lines a request's state needs under its places, in the approved words. With `paperRoute` (the
 * sender, who can cancel it), a decline is followed by the paper route's line (W17, frame 90): the
 * copy they sign outside Givmo is recorded once the request is cancelled.
 */
export function statusLines(
  detail: SigningRequestDetail,
  personName: ((userId: string) => string | undefined) | undefined,
  { paperRoute = false }: { paperRoute?: boolean } = {},
): StatusLine[] {
  const zone = detail.version.timeZone;
  if (detail.request.state === 'expired') return [{ text: expiredLine(detail) }];
  if (detail.request.state === 'cancelled') {
    return [{ text: cancelledLine(detail, personName) }];
  }
  if (detail.request.state !== 'open') return [];
  return detail.slots.flatMap((slot): StatusLine[] => {
    const name = nameOf(personName, slot.signerUserId);
    if (slot.signedThenLostAccess) {
      return [{ text: signedThenLostLine(name, slot.standingActAt, zone) }];
    }
    if (slot.standing !== 'signed' && slot.cannotSignLostAccess) {
      return [{ text: cantSignLine(name) }];
    }
    if (slot.standing === 'declined') {
      const decline = latestActOn(detail, slot, 'decline');
      const line = {
        text: declinedLine(name, decline?.occurredAt ?? slot.standingActAt, decline?.reason, zone),
      };
      return paperRoute
        ? [line, { text: declinedPaperLine(personName?.(slot.signerUserId)), paperRoute: true }]
        : [line];
    }
    return [];
  });
}

/** The status card (frames 41–44): open, expired or cancelled. */
export function RequestStatusCard({
  detail,
  sender,
  offerSend,
  sendDisabled,
  sending,
  onSend,
  onChangeSigner,
  onChanged,
}: {
  detail: SigningRequestDetail;
  /** Whether the viewer may extend, change or cancel it (a sender), and send again once it ended. */
  sender: boolean;
  /** Whether "Send for signature" shows on an ended request (a writer: a Member sees it disabled). */
  offerSend: boolean;
  sendDisabled: boolean;
  sending: boolean;
  onSend: () => void;
  onChangeSigner: () => void;
  /** The request as an act answered it (or nothing, to read it again). */
  onChanged: (answer: unknown) => void;
}) {
  const { orgRef, operations, personName } = useCompliance();
  const [extending, setExtending] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [day, setDay] = useState('');
  const requestId = detail.request.requestId;
  const open = detail.request.state === 'open';
  // A refusal because the request moved on (completed, cancelled, revised or expired meanwhile):
  // the card reads it again, so the card around the served line is the request as it is now.
  const rereadOnConflict = (error: unknown) => {
    if (isActConflict(error)) onChanged(undefined);
  };

  const extend = useMutation({
    mutationFn: () =>
      operations.extendSigningRequest(orgRef ?? '', requestId, {
        expires_at: signByInstant(day, new Date(), detail.version.timeZone) ?? '',
      }),
    onSuccess: (answer) => {
      setExtending(false);
      onChanged(answer);
    },
    onError: rereadOnConflict,
  });
  const cancel = useMutation({
    mutationFn: () => operations.cancelSigningRequest(orgRef ?? '', requestId, { reason: null }),
    onSuccess: (answer) => {
      setCancelling(false);
      onChanged(answer);
    },
    onError: rereadOnConflict,
  });

  return (
    <Card className="flex flex-col gap-14 px-20 py-16" testId={PREFIX}>
      <div className="flex items-start justify-between gap-16">
        <div className="flex min-w-0 flex-col gap-2">
          <h2 className="text-s16 font-w600 text-text-strong" {...testIdProp(`${PREFIX}-heading`)}>
            {statusHeading(detail)}
          </h2>
          <p className="text-s13 text-text-secondary" {...testIdProp(`${PREFIX}-sent`)}>
            {sentLine(detail, personName)}
          </p>
        </div>
        {open && sender && (
          <Menu.Root modal={false}>
            <Menu.Trigger
              saas
              render={<IconButton size="sm" tone="ghost" aria-label={MORE_FOR_THIS_FILING} />}
              testId={`${PREFIX}-menu`}
            >
              <Icon icon={MoreHorizontal} size={18} aria-hidden />
            </Menu.Trigger>
            <Menu.Content saas className="min-w-[232px]">
              <Menu.Item
                saas
                onClick={() => {
                  setDay(currentSignByDay(detail));
                  extend.reset();
                  setExtending(true);
                }}
                testId={`${PREFIX}-extend`}
              >
                {EXTEND_THE_DATE}
              </Menu.Item>
              <Menu.Item saas onClick={onChangeSigner} testId={`${PREFIX}-change-signer`}>
                {CHANGE_THE_SIGNER}
              </Menu.Item>
              <Menu.Item
                saas
                onClick={() => {
                  cancel.reset();
                  setCancelling(true);
                }}
                testId={`${PREFIX}-cancel`}
              >
                {CANCEL_THE_REQUEST}
              </Menu.Item>
            </Menu.Content>
          </Menu.Root>
        )}
        {!open && offerSend && (
          <Button
            appearance="saas"
            tone="primary"
            size="sm"
            disabled={sendDisabled}
            loading={sending}
            onClick={onSend}
            testId={`${PREFIX}-send`}
          >
            {SEND_FOR_SIGNATURE}
          </Button>
        )}
      </div>
      {detail.slots.map((slot) => (
        <div
          key={slot.slotId}
          className="flex flex-wrap items-center justify-between gap-x-16 gap-y-4 rounded-lg border border-border-subtle px-16 py-12"
          {...testIdProp(`${PREFIX}-place`)}
        >
          <div className="flex min-w-0 flex-col gap-2">
            <p className="text-s14 text-text">{nameOf(personName, slot.signerUserId)}</p>
            <p className="text-s12 text-text-tertiary">{placeSubLine(slot)}</p>
          </div>
          <span className="text-s13 text-text-secondary" {...testIdProp(`${PREFIX}-place-state`)}>
            {placeState(detail, slot)}
          </span>
        </div>
      ))}
      {statusLines(detail, personName, { paperRoute: sender }).map((line, index) =>
        line.paperRoute ? (
          // W17 (frame 90): tight under its decline, a shade quieter.
          <p
            key={index}
            className="-mt-8 text-s13 leading-h15 text-text-secondary"
            {...testIdProp(`${PREFIX}-paper-route`)}
          >
            {line.text}
          </p>
        ) : (
          // A decline's reason keeps its line breaks (D-CCM-35 item 5 (C)).
          <p
            key={index}
            className="whitespace-pre-line text-s13 leading-h15 text-text"
            {...testIdProp(`${PREFIX}-line`)}
          >
            {line.text}
          </p>
        ),
      )}

      <Dialog.Root
        open={extending}
        onOpenChange={(next) => {
          if (!next && !extend.isPending) setExtending(false);
        }}
      >
        {extending && (
          <Dialog.Content
            width={440}
            className="flex flex-col gap-16"
            testId={`${PREFIX}-extend-dialog`}
          >
            <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
              {EXTEND_THE_DATE}
            </Dialog.Title>
            <ComplianceDateField
              label={SIGN_BY}
              long
              value={day}
              onChange={setDay}
              testId={`${PREFIX}-extend-sign-by`}
            />
            {extend.error !== null && extend.error !== undefined && (
              <ErrorMessage testId={`${PREFIX}-extend-failure`}>
                {serverMessage(extend.error, RECORD_FAILED)}
              </ErrorMessage>
            )}
            <div className="flex justify-end gap-10">
              <Button
                appearance="saas"
                tone="secondary"
                type="button"
                onClick={() => setExtending(false)}
                disabled={extend.isPending}
                testId={`${PREFIX}-extend-keep`}
              >
                {CANCEL}
              </Button>
              <Button
                appearance="saas"
                tone="primary"
                type="button"
                disabled={signByInstant(day, new Date(), detail.version.timeZone) === undefined}
                loading={extend.isPending}
                onClick={() => extend.mutate()}
                testId={`${PREFIX}-extend-confirm`}
              >
                {EXTEND}
              </Button>
            </div>
          </Dialog.Content>
        )}
      </Dialog.Root>

      <ConfirmDialog
        open={cancelling}
        title={CANCEL_THIS_REQUEST}
        body={cancelRequestBody(detail, personName)}
        confirm={CANCEL_THE_REQUEST}
        pending={cancel.isPending}
        error={cancel.error}
        onConfirm={() => cancel.mutate()}
        onClose={() => setCancelling(false)}
        testId={`${PREFIX}-cancel-dialog`}
      />
    </Card>
  );
}

/** A dialog that asks once before an act that cannot be taken back (approved words only). */
function ConfirmDialog({
  open,
  title,
  body,
  confirm,
  pending,
  error,
  onConfirm,
  onClose,
  testId,
}: {
  open: boolean;
  title: string;
  body: string;
  confirm: string;
  pending: boolean;
  error: unknown;
  onConfirm: () => void;
  onClose: () => void;
  testId: string;
}) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
    >
      {open && (
        <Dialog.Content width={440} className="flex flex-col gap-16" testId={testId}>
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {title}
          </Dialog.Title>
          <Dialog.Description className="text-s14 text-text-secondary">{body}</Dialog.Description>
          {error !== null && error !== undefined && (
            <ErrorMessage testId={`${testId}-failure`}>
              {serverMessage(error, RECORD_FAILED)}
            </ErrorMessage>
          )}
          <div className="flex justify-end gap-10">
            <Button
              appearance="saas"
              tone="secondary"
              type="button"
              onClick={onClose}
              disabled={pending}
              testId={`${testId}-keep`}
            >
              {KEEP_IT}
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              type="button"
              loading={pending}
              onClick={onConfirm}
              testId={`${testId}-confirm`}
            >
              {confirm}
            </Button>
          </div>
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}

/**
 * "Change an answer?" (frames 41, 42, 44's lock line): cancelling the request first, so no one can
 * sign content that is being changed (design §4.4's attributed cancel).
 */
export function ChangeAnswerDialog({
  detail,
  open,
  onClose,
  onChanged,
}: {
  detail: SigningRequestDetail;
  open: boolean;
  onClose: () => void;
  onChanged: (answer: unknown) => void;
}) {
  const { orgRef, operations, personName } = useCompliance();
  const cancel = useMutation({
    mutationFn: () =>
      operations.cancelSigningRequest(orgRef ?? '', detail.request.requestId, { reason: null }),
    onSuccess: (answer) => {
      onClose();
      onChanged(answer);
    },
    // The request moved on meanwhile: read it again behind the served line.
    onError: (error) => {
      if (isActConflict(error)) onChanged(undefined);
    },
  });
  return (
    <ConfirmDialog
      open={open}
      title={CHANGE_AN_ANSWER_TITLE}
      body={changeAnswerBody(detail, personName)}
      confirm={CANCEL_AND_CHANGE}
      pending={cancel.isPending}
      error={cancel.error}
      onConfirm={() => cancel.mutate()}
      onClose={() => {
        cancel.reset();
        onClose();
      }}
      testId="compliance-change-answer-dialog"
    />
  );
}

/** "A · B" served digests kept whole on one line (`f0d8…09f7`), never broken at the ellipsis. */
function keepDigestsWhole(line: string) {
  const parts = line.split(/([0-9a-f]{4}…[0-9a-f]{4})/);
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className="whitespace-nowrap">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

/** The signed form and its signing record (frame 46), once the request completes. */
export function SignedFormCards({
  detail,
  ledger,
}: {
  detail: SigningRequestDetail;
  /** The panel's four lines for the filing: label and value. */
  ledger: readonly (readonly [string, string])[];
}) {
  const { orgRef, isReady, operations, canSendForSignature } = useCompliance();
  const ref = isReady ? orgRef : null;
  const requestId = detail.request.requestId;
  const [removing, setRemoving] = useState(false);
  const record = useQuery({
    queryKey: signingRecordQueryKey(ref, requestId),
    queryFn: () => operations.getSigningRecordLines(ref ?? '', requestId),
    enabled: ref !== null,
  });
  const decoded = decodeSigningRecordLines(record.data);
  const lines = decoded?.lines ?? [];
  // On a package of several documents, each document's lines under its title (R6, D-CCM-101); one
  // document keeps frame 46's lines, split as below.
  const sections =
    decoded !== undefined && decoded.documents.length > 1 ? decoded.documents : undefined;
  // The record's lines as served: the sent line, each signer's two, each document's form line, then
  // what the record doesn't show. The form lines read a shade quieter, the last quieter still.
  const forms = detail.documents.length;
  const body = lines.slice(0, Math.max(0, lines.length - 1 - forms));
  const formLines = lines.slice(Math.max(0, lines.length - 1 - forms), -1);
  const statement = lines.at(-1);

  const download = useMutation({
    mutationFn: async (which: 'pdf' | 'json' | 'export') => {
      const answer =
        which === 'export'
          ? await operations.getSigningExport(ref ?? '', requestId)
          : await operations.getSigningEvidence(ref ?? '', requestId, which);
      const file = decodeSigningFile(answer);
      if (file === undefined) throw new Error(OPEN_FAILED);
      return file.downloadUrl;
    },
    // Each link is spent here: the next click asks for a fresh one, and the server logs each.
    onSuccess: (url) => window.location.assign(url),
    onError: (cause) => toast.error(serverMessage(cause, OPEN_FAILED)),
  });

  return (
    <div className="grid gap-16 lg:grid-cols-2" {...testIdProp('compliance-signed')}>
      <Card className="flex flex-col gap-14 px-20 py-16" testId="compliance-signed-form">
        <div className="flex flex-col gap-2">
          <h2 className="text-s16 font-w600 text-text-strong">{THE_SIGNED_FORM}</h2>
          <p className="text-s13 text-text-secondary">{signedOnLine(detail)}</p>
        </div>
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-20 gap-y-8 text-s13">
          {ledger.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-text-tertiary">{label}</dt>
              <dd className="text-text">{value}</dd>
            </div>
          ))}
        </dl>
        <div className="flex flex-wrap gap-8 pt-2">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            leadingIcon={<Icon icon={Download} size={14} aria-hidden />}
            loading={download.isPending && download.variables === 'export'}
            onClick={() => download.mutate('export')}
            testId="compliance-signed-export"
          >
            {EXPORT_EVERYTHING}
          </Button>
        </div>
      </Card>
      <Card className="flex flex-col gap-12 px-20 py-16" testId="compliance-signing-record">
        <div className="flex items-start justify-between gap-12">
          <h2 className="text-s16 font-w600 text-text-strong">{SIGNING_RECORD}</h2>
          {canSendForSignature === true && (
            // The button is taller than the heading's line: it overhangs it, so the card's lines
            // start where they do without it.
            <div className="-my-4 flex shrink-0 items-center">
              <Menu.Root modal={false}>
                <Menu.Trigger
                  saas
                  render={<IconButton size="sm" tone="ghost" aria-label={MORE_FOR_THIS_RECORD} />}
                  testId="compliance-signing-record-menu"
                >
                  <Icon icon={MoreHorizontal} size={18} aria-hidden />
                </Menu.Trigger>
                <Menu.Content saas className="min-w-[232px]">
                  <Menu.Item
                    saas
                    tone="danger"
                    onClick={() => setRemoving(true)}
                    testId="compliance-signing-record-remove-details"
                  >
                    {REMOVE_SIGN_IN_DETAILS}
                  </Menu.Item>
                </Menu.Content>
              </Menu.Root>
            </div>
          )}
        </div>
        {sections !== undefined ? (
          <div className="flex flex-col gap-12">
            <p className="text-s13 leading-h15 text-text">{lines[0]}</p>
            {sections.map((section, sectionIndex) => (
              <section
                key={sectionIndex}
                className="flex flex-col gap-8 pt-4"
                {...testIdProp('compliance-signing-record-document')}
              >
                <h3 className="text-s13 font-w600 leading-h15 text-text-strong">{section.title}</h3>
                <ul className="flex flex-col gap-8 text-s13 leading-h15 text-text">
                  {/* Its form line, last, reads a shade quieter, as frame 46's do. */}
                  {section.lines.map((line, index) => (
                    <li
                      key={index}
                      className={
                        index === section.lines.length - 1 ? 'text-text-secondary' : undefined
                      }
                    >
                      {keepDigestsWhole(line)}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ) : (
          lines.length > 0 && (
            <ul className="flex flex-col gap-8 text-s13 leading-h15 text-text">
              {body.map((line, index) => (
                <li key={`b${index}`}>{keepDigestsWhole(line)}</li>
              ))}
              {formLines.map((line, index) => (
                <li key={`f${index}`} className="text-text-secondary">
                  {keepDigestsWhole(line)}
                </li>
              ))}
            </ul>
          )
        )}
        {statement !== undefined && (
          <p
            className="text-s12 leading-h15 text-text-tertiary"
            {...testIdProp('compliance-signing-record-statement')}
          >
            {statement}
          </p>
        )}
        <div className="flex flex-wrap gap-8 pt-2">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            loading={download.isPending && download.variables === 'pdf'}
            onClick={() => download.mutate('pdf')}
            testId="compliance-signing-record-pdf"
          >
            {DOWNLOAD_RECORD_PDF}
          </Button>
          <Button
            appearance="saas"
            tone="ghost"
            size="sm"
            loading={download.isPending && download.variables === 'json'}
            onClick={() => download.mutate('json')}
            testId="compliance-signing-record-json"
          >
            {DOWNLOAD_RECORD_JSON}
          </Button>
        </div>
      </Card>
      {removing && (
        <RemoveSignInDetailsDialog
          detail={detail}
          open={removing}
          onClose={() => setRemoving(false)}
        />
      )}
    </div>
  );
}
